/* Two spectrogram views over one renderer and one colour scale.
 *
 * Live is a waterfall fed by an AnalyserNode; Static is a whole decoded file
 * transformed once. They deliberately look identical, because they are the same
 * measurement of the same thing and a different palette for each would imply
 * otherwise.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { F_MIN, autoRange, axisTicks, paintColumn, type Spectrogram as Spec } from '../dsp'
import { describeHz, visibleBands } from '../bands'

/* Defaults chosen from what a hydrophone actually produces: an uncalibrated
 * recorder sitting in a quiet room or a quiet fjord lands around -75 dBFS
 * broadband, and clipping is 0. Showing -100..0 would spend two-thirds of the
 * scale on nothing. */
export const DEFAULT_MIN_DB = -95
export const DEFAULT_MAX_DB = -20

function Axis({ sampleRate }: { sampleRate: number }) {
  return (
    <div className="spec-axis" aria-hidden="true">
      {axisTicks(sampleRate).map((t) => (
        <span key={t.label} style={{ top: `${t.frac * 100}%` }}>
          {t.label}
        </span>
      ))}
    </div>
  )
}

/** The one place the dBFS/µPa distinction is stated, so it cannot be forgotten
 *  on one view and remembered on another. `basis` comes from the server. */
export function SpecLegend({
  basis, absolute, sampleRate, note, minDb = DEFAULT_MIN_DB, maxDb = DEFAULT_MAX_DB,
}: {
  basis: string
  absolute: boolean
  sampleRate: number
  note?: string
  /** Stated rather than assumed. The live view measures its own range from the
   *  signal, so the legend has to report what is actually being drawn. */
  minDb?: number
  maxDb?: number
}) {
  return (
    <div className="spec-foot">
      <span className="scale-bar" role="img" aria-label="quiet to loud" />
      <span className="num">
        {minDb} to {maxDb} dB · {basis}
      </span>
      <span className="faint">
        {F_MIN} Hz – {(sampleRate / 2 / 1000).toFixed(1)} kHz, log
      </span>
      {!absolute && (
        <span className="badge" title="R5.3: levels are site-relative until the compute full-scale voltage, the mission analogue gain and a dated microphone calibration are all known.">
          not calibrated
        </span>
      )}
      {note && <span className="faint">{note}</span>}
    </div>
  )
}

/* ------------------------------------------------------------------- live */

export function LiveSpectrogram({
  analyser, height = 280, minDb = DEFAULT_MIN_DB, maxDb = DEFAULT_MAX_DB,
}: {
  analyser: AnalyserNode | null
  height?: number
  minDb?: number
  maxDb?: number
}) {
  const ref = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = ref.current
    if (!canvas || !analyser) return

    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) return

    const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
    // Step by whole device pixels so the waterfall stays crisp, but by `dpr` of
    // them so the time scale is the same on a laptop and an external monitor.
    const step = Math.round(dpr)
    let w = 0
    let h = 0
    // Recreated on every resize. Holding one ImageData for the lifetime of the
    // effect would silently stop the waterfall the first time the window
    // changed width, because its height would no longer match the canvas.
    let col: ImageData | null = null

    const resize = () => {
      const rect = canvas.getBoundingClientRect()
      const nw = Math.max(64, Math.floor(rect.width * dpr))
      const nh = Math.max(1, Math.floor(height * dpr))
      if (nw === w && nh === h) return
      // Preserve what is already drawn across a resize, otherwise dragging a
      // window edge wipes the history the user was watching.
      const prev = w && h ? ctx.getImageData(0, 0, w, h) : null
      w = canvas.width = nw
      h = canvas.height = nh
      ctx.fillStyle = '#04150f'
      ctx.fillRect(0, 0, w, h)
      if (prev) ctx.putImageData(prev, 0, 0)
      col = ctx.createImageData(step, h)
    }
    resize()

    const ro = new ResizeObserver(resize)
    ro.observe(canvas)

    const bins = new Float32Array(analyser.frequencyBinCount)
    const sampleRate = analyser.context.sampleRate
    let raf = 0

    const frame = () => {
      raf = requestAnimationFrame(frame)
      if (!w || !h || !col) return

      // Float, not byte: getByteFrequencyData pre-normalises against the
      // analyser's own min/max and throws away the actual dB value, which is
      // the number we want to show and to scale honestly.
      analyser.getFloatFrequencyData(bins)

      // Scroll left one step, then draw the newest column at the right edge.
      ctx.drawImage(canvas, -step, 0)

      for (let s = 0; s < step; s++) {
        paintColumn(col.data, step, h, s, bins, sampleRate, minDb, maxDb)
      }
      ctx.putImageData(col, w - step, 0)
    }
    raf = requestAnimationFrame(frame)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
    }
  }, [analyser, height, minDb, maxDb])

  return (
    <div className="spec-wrap" style={{ height }}>
      <canvas ref={ref} className="spec-canvas" />
      {analyser && <Axis sampleRate={analyser.context.sampleRate} />}
    </div>
  )
}

/* ----------------------------------------------------------------- static */

/** Frequency at a fraction from the top of the canvas. The inverse of the log
 *  mapping `rowToBin` paints with, so the readout names the row it points at
 *  rather than an approximation of it. */
function hzAtFrac(frac: number, sampleRate: number): number {
  const nyq = sampleRate / 2
  return F_MIN * Math.pow(nyq / F_MIN, 1 - frac)
}

function fmtHz(f: number): string {
  if (f >= 10000) return `${(f / 1000).toFixed(0)} kHz`
  if (f >= 1000) return `${(f / 1000).toFixed(1)} kHz`
  return `${Math.round(f)} Hz`
}

function fmtClock(s: number): string {
  const m = Math.floor(s / 60)
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, '0')}`
}

export function StaticSpectrogram({
  spec, height = 300, progress = null, minDb, maxDb,
  onSeek, showBands = true, onRange,
}: {
  spec: Spec
  height?: number
  /** 0..1 playback position, or null when not playing. */
  progress?: number | null
  /** Override the measured range. Omitted is the normal case: a fixed pair
   *  cannot fit both a clip captured at +50 dB and one captured at unity. */
  minDb?: number
  maxDb?: number
  /** The range actually drawn, so the legend can state it rather than print a
   *  constant that stopped being true. */
  onRange?: (lo: number, hi: number) => void
  onSeek?: (fraction: number) => void
  /** The what-lives-here overlay. See src/bands.ts for why it says nothing
   *  about level. */
  showBands?: boolean
}) {
  const ref = useRef<HTMLCanvasElement>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  /* The visible slice of the clip, as fractions of its duration.
   *
   * Zoom exists because the interesting things live at different scales: a
   * vessel tonal at 80 Hz is a minute-long smear, and a snapping shrimp is
   * four milliseconds. One fixed view can show one of them. */
  const [view, setView] = useState<[number, number]>([0, 1])
  const [hover, setHover] = useState<{ xf: number; yf: number } | null>(null)
  const drag = useRef<{ x: number; view: [number, number] } | null>(null)
  const moved = useRef(false)

  const [v0, v1] = view
  const span = Math.max(1e-4, v1 - v0)
  const zoomed = span < 0.999

  // Measured once per clip, not per frame.
  const [lo, hi] = useMemo(() => {
    const [a, b] = autoRange(spec)
    return [minDb ?? a, maxDb ?? b]
  }, [spec, minDb, maxDb])

  const reported = useRef<((lo: number, hi: number) => void) | undefined>(undefined)
  reported.current = onRange
  useEffect(() => { reported.current?.(lo, hi) }, [lo, hi])

  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) return

    const draw = () => {
      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1))
      const rect = canvas.getBoundingClientRect()
      const w = Math.max(64, Math.floor(rect.width * dpr))
      const h = Math.floor(height * dpr)
      canvas.width = w
      canvas.height = h

      const img = ctx.createImageData(w, h)
      const n = spec.columns.length
      for (let x = 0; x < w; x++) {
        // Nearest-neighbour in time. The alternative -- averaging the columns
        // that fall in a pixel -- smooths transients away, and a transient is
        // usually the thing being looked for.
        //
        // Re-read from the FFT columns at every zoom level rather than scaling
        // the previous bitmap: zooming in therefore reveals detail that was
        // always in the data instead of magnifying pixels, which is the whole
        // reason to offer zoom at all.
        const t = v0 + (x / w) * span
        const ci = n === 1 ? 0 : Math.min(n - 1, Math.floor(t * n))
        paintColumn(img.data, w, h, x, spec.columns[ci]!, spec.sampleRate, lo, hi)
      }
      ctx.putImageData(img, 0, 0)
    }

    draw()
    const ro = new ResizeObserver(draw)
    ro.observe(canvas)
    return () => ro.disconnect()
  }, [spec, height, lo, hi, v0, span])

  /* Wheel to zoom, anchored on the pointer.
   *
   * Registered non-passively so the page does not scroll underneath the
   * gesture -- on a laptop trackpad that is the difference between examining a
   * transient and losing your place entirely. */
  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      const at = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
      setView(([a, b]) => {
        const s = b - a
        const anchor = a + at * s
        // Capped at 500x. Past that a pixel is narrower than the hop between
        // FFT columns and the picture stops gaining information.
        const ns = Math.min(1, Math.max(1 / 500, s * (e.deltaY > 0 ? 1.25 : 0.8)))
        let na = anchor - at * ns
        na = Math.min(1 - ns, Math.max(0, na))
        return [na, na + ns]
      })
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [])

  const bands = showBands ? visibleBands(spec.sampleRate) : []
  const nyq = spec.sampleRate / 2
  const logSpan = Math.log(nyq / F_MIN)
  const fracOf = (f: number) => 1 - Math.log(Math.max(F_MIN, f) / F_MIN) / logSpan

  const hoverHz = hover ? hzAtFrac(hover.yf, spec.sampleRate) : 0
  const hoverT = hover ? (v0 + hover.xf * span) * spec.duration : 0

  return (
    <div
      ref={wrapRef}
      className="spec-wrap"
      style={{ height, cursor: drag.current ? 'grabbing' : onSeek ? 'crosshair' : 'default' }}
      onPointerDown={(e) => {
        drag.current = { x: e.clientX, view }
        moved.current = false
        e.currentTarget.setPointerCapture(e.pointerId)
      }}
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect()
        setHover({
          xf: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
          yf: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
        })
        const d = drag.current
        if (!d) return
        const dx = (e.clientX - d.x) / r.width
        if (Math.abs(dx) > 0.002) moved.current = true
        const s = d.view[1] - d.view[0]
        let a = d.view[0] - dx * s
        a = Math.min(1 - s, Math.max(0, a))
        setView([a, a + s])
      }}
      onPointerUp={(e) => {
        const wasDrag = moved.current
        drag.current = null
        e.currentTarget.releasePointerCapture(e.pointerId)
        // A drag that panned is not also a seek. Without this, letting go
        // after moving the view jumps playback to wherever the finger landed.
        if (!wasDrag && onSeek) {
          const r = e.currentTarget.getBoundingClientRect()
          const xf = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
          onSeek(Math.min(1, Math.max(0, v0 + xf * span)))
        }
      }}
      onPointerLeave={() => { setHover(null); drag.current = null }}
      onDoubleClick={() => setView([0, 1])}
    >
      {/* Annotation lives in the margin, not over the data.
          Drawn on top of the picture it collided with the frequency axis and
          was illegible against both the dark and the bright parts of the image
          -- which is why print puts callouts in the gutter and rules them back
          to what they name. */}
      {bands.length > 0 && (
        <div className="spec-bands" aria-hidden="true">
          {bands.map((b) => {
            const top = fracOf(b.hi)
            const bot = fracOf(b.lo)
            return (
              <span
                key={`${b.kind}-${b.label}`}
                className={`sb ${b.kind}`}
                style={{ top: `${top * 100}%`, height: `${(bot - top) * 100}%` }}
                title={`${b.label} — ${b.meaning}`}
              >
                <i />
                <em>{b.label}</em>
              </span>
            )
          })}
        </div>
      )}

      <canvas ref={ref} className="spec-canvas" />
      <Axis sampleRate={spec.sampleRate} />

      {/* Crosshair and readout. The chart could already be looked at; this is
          what lets a point in it be quoted -- in a report, in an email, to a
          vet. Without it every observation is "somewhere around there". */}
      {hover && (
        <>
          <div className="spec-cross h" style={{ top: `${hover.yf * 100}%` }} />
          <div className="spec-cross v" style={{ left: `${hover.xf * 100}%` }} />
          <div
            className="spec-read"
            style={{
              left: `${hover.xf * 100}%`,
              top: `${hover.yf * 100}%`,
              // Flip before the box would leave the canvas, so the readout
              // never hides the thing being read.
              transform: `translate(${hover.xf > 0.62 ? '-104%' : '8px'}, ${hover.yf > 0.7 ? '-118%' : '8px'})`,
            }}
          >
            <b className="num">{fmtHz(hoverHz)}</b>
            <span className="num faint">{fmtClock(hoverT)}</span>
            <em>{describeHz(hoverHz)}</em>
          </div>
        </>
      )}

      {progress !== null && progress >= 0 && progress >= v0 && progress <= v1 && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', top: 0, bottom: 0,
            left: `${((progress - v0) / span) * 100}%`, width: 1,
            background: 'rgba(230,241,234,0.9)',
            boxShadow: '0 0 6px rgba(230,241,234,0.6)',
            pointerEvents: 'none',
          }}
        />
      )}

      {zoomed && (
        <button className="btn ghost sm spec-reset"
                onClick={(e) => { e.stopPropagation(); setView([0, 1]) }}>
          {(1 / span).toFixed(0)}× · reset
        </button>
      )}
    </div>
  )
}
